import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query'
import { getAuthorizations } from 'src/common/api-clients/github'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import useGetContract from 'src/common/contracts/hooks/common/useGetContract'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import { isAccountAuthorized as _isAccountAuthorized } from 'src/thirdweb/auth'
import { useActiveAccount } from 'thirdweb/react'
import { normalizeAuthorizations } from './utils'
import { areAddressesEqual } from 'src/common/utils/web3/areAddressesEqual'
import { Authorization } from 'src/common/api-clients/github/types'

type AuthorizationsQueryOptions = Omit<
  UndefinedInitialDataOptions<Authorization[]>,
  'queryFn' | 'queryKey'
>

const useAuthorizations = (
  authAddresses?: string[] | null,
  options?: AuthorizationsQueryOptions
) => {
  const { enabled = true } = options || {}
  const chain = useActiveOrDefaultChain()
  const { getContract } = useGetContract()
  const account = useActiveAccount()
  const { smartAccountInfo, isLoading: smartAccountLoading } = useSmartAccount()

  const checkAuthorization = async (authContractAddress: string) => {
    const contract = getContract(authContractAddress)

    let isAccountAuthorized = false
    let isSmartAccountAuthorized = false

    if (account) {
      isAccountAuthorized = await _isAccountAuthorized({
        contract,
        account: account.address,
      })
    }
    if (smartAccountInfo?.address) {
      isSmartAccountAuthorized = await _isAccountAuthorized({
        contract,
        account: smartAccountInfo.address,
      })
    }

    return {
      isAccountAuthorized,
      isSmartAccountAuthorized,
      isFullyAuthorized: isAccountAuthorized && isSmartAccountAuthorized,
    }
  }

  const { data: authorizations, isLoading } = useQuery<Authorization[]>({
    queryFn: async () => {
      const authorizations = await getAuthorizations()

      const normalizedAuthorizations = normalizeAuthorizations(
        authorizations,
        chain.id
      )

      const authorizationsWithStatus = await Promise.all(
        normalizedAuthorizations?.map(async auth => {
          const {
            isAccountAuthorized,
            isSmartAccountAuthorized,
            isFullyAuthorized,
          } = await checkAuthorization(auth.authContractAddress)

          return {
            ...auth,
            isFullyAuthorized,
            isAccountAuthorized,
            isSmartAccountAuthorized,
          }
        }) ?? []
      )

      return authorizationsWithStatus
    },
    queryKey: [
      'authorizations',
      chain.id,
      account?.address,
      smartAccountInfo?.address,
    ],
    staleTime: 100000,
    ...options,
    enabled: enabled && !!smartAccountInfo?.address && !!account?.address,
  })

  const { data: notRegisteredAuths, isLoading: notRegisteredAuthsLoading } =
    useQuery<Authorization[]>({
      queryFn: async () => {
        const notRegisteredAuthAddresses = authAddresses?.filter(
          authAddress =>
            !authorizations?.some(auth =>
              areAddressesEqual(auth.authContractAddress, authAddress)
            )
        )

        const authorizationsWithStatus = await Promise.all(
          notRegisteredAuthAddresses?.map(async authContractAddress => {
            const {
              isAccountAuthorized,
              isSmartAccountAuthorized,
              isFullyAuthorized,
            } = await checkAuthorization(authContractAddress)

            return {
              name: '',
              chainId: chain.id,
              linkToQualify: '',
              authContractAddress,
              isFullyAuthorized,
              isAccountAuthorized,
              isSmartAccountAuthorized,
            }
          }) ?? []
        )

        return authorizationsWithStatus
      },
      queryKey: [
        'notRegisteredAuthorizations',
        chain.id,
        account?.address,
        smartAccountInfo?.address,
        authAddresses,
      ],
      staleTime: 100000,
      ...options,
      enabled: Boolean(
        enabled &&
          smartAccountInfo?.address &&
          account?.address &&
          authorizations
      ),
    })

  const allAuthorizations = [
    ...(authorizations ?? []),
    ...(notRegisteredAuths ?? []),
  ]

  const filterAuthorizationsByAddresses = (addresses: string[]) =>
    allAuthorizations.filter(auth =>
      addresses.some(address =>
        areAddressesEqual(address, auth.authContractAddress)
      )
    )

  const checkAddressesAreAuthorized = (authAddresses: string[]) =>
    filterAuthorizationsByAddresses(authAddresses)?.every(
      auth => auth.isFullyAuthorized
    )

  const checkOneOfAddressesIsAuthorized = (authAddresses: string[]) =>
    filterAuthorizationsByAddresses(authAddresses)?.some(
      auth => auth.isFullyAuthorized
    )

  const getAuthorization = (authAddress: string) =>
    allAuthorizations.find(auth =>
      areAddressesEqual(auth.authContractAddress, authAddress)
    )

  return {
    authorizations,
    notRegisteredAuths,
    allAuthorizations,
    filterAuthorizationsByAddresses,
    checkAddressesAreAuthorized,
    checkOneOfAddressesIsAuthorized,
    getAuthorization,
    isLoading: smartAccountLoading || isLoading || notRegisteredAuthsLoading,
  }
}

export default useAuthorizations
